// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { ChatKit, useChatKit } from '@openai/chatkit-react';

import { getCsrfToken } from './chatkit/getCsrfToken.js';
import { getLocale } from './chatkit/getLocale.js';
import { getPreferredColorScheme, prefersDarkMediaQuery } from './chatkit/getPreferredColorScheme.js';
import { getCurrentContext } from './chatkit/getCurrentContext.js';
import { getBrowserInfo } from './chatkit/getBrowserInfo.ts';
import { createSupportIssue, createSupportIssueFromWidget } from './chatkit/createSupportIssue.ts';
import { updateStepInputFrame } from './chatkit/updateStepInputFrame.js';
import { getInitialThreadId, saveLastThread } from './chatkit/threadMemory.js';
import startScreen from './chatkit/startScreen.ts';

/**
 * @returns {string} 'light' or 'dark'
 */
const preferredColorScheme = () => {
  const [scheme, setScheme] = useState(() => getPreferredColorScheme());

  useEffect(() => {
    if (typeof globalThis === 'undefined' || typeof globalThis.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = globalThis.matchMedia(prefersDarkMediaQuery);
    const handleChange = (event) => setScheme(event.matches ? 'dark' : 'light');

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return scheme;
};

const callBackendTool = async (name, params) => {
  const response = await fetch(`/chat_kit_tools/${encodeURIComponent(name)}/call`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': getCsrfToken()
    },
    credentials: 'same-origin',
    body: JSON.stringify(params || {}),
  });

  if (response.status === 404) {
    throw new Error(`Tool not found: ${name}`);
  }

  let json = null;
  try {
    json = await response.json();
  } catch {
    // If server didn't return JSON, keep json as null.
  }

  if (response.status === 400) {
    const msg = json?.error || `Bad request for tool: ${name}`;
    throw new Error(msg);
  }

  if (!response.ok) {
    throw new Error(json?.error || `Tool call failed: ${name} (${response.status})`);
  }

  return json || {};
};

const ChatKitComponent = ({ domainKey }) => {
  // Get the last thread ID if it's recent enough (within 24 hours by default)
  const [initialThreadId] = useState(() => getInitialThreadId(24));
  // Track current thread ID for use in client tools
  const [currentThreadId, setCurrentThreadId] = useState(initialThreadId);

  const { control } = useChatKit({
    locale: getLocale(),
    theme: preferredColorScheme(),
    initialThread: initialThreadId,
    startScreen,
    api: {
      url: '/chat_kit',
      domainKey,
      fetch: (input, init = {}) =>
        fetch(input, {
          ...init,
          credentials: 'same-origin',
          headers: {
            ...(init.headers || {}),
            'X-CSRF-Token': getCsrfToken(),
          },
        }),
    },
    onClientTool: async ({ name, params }) => {
      if (name === 'get_current_context') {
        return getCurrentContext();
      }

      if (name === 'get_browser_info') {
        return getBrowserInfo();
      }

      if (name === 'create_support_issue') {
        return createSupportIssue(params, currentThreadId);
      }

      const result = await callBackendTool(name, params);

      if (name === 'update_step') {
        updateStepInputFrame(params.step_id);
      }

      return result;
    },
    onThreadChange: ({ threadId }) => {
      // Persist only real thread ids (null = new empty thread view).
      if (threadId) {
        saveLastThread(threadId);
        setCurrentThreadId(threadId);
      } else {
        setCurrentThreadId(null);
      }
    },
    onThreadLoadEnd: ({ threadId }) => {
      if (threadId) {
        saveLastThread(threadId);
        setCurrentThreadId(threadId);
      }
    },
    widgets: {
      onAction: async (action) => {
        if (action.type === 'supportIssue.create') {
          await createSupportIssueFromWidget(action.payload, currentThreadId);
        }
      }
    }
  });

  return (
    <div className="h-full w-full">
      <ChatKit control={control} className="h-full w-full" />
    </div>
  );
};

export default ChatKitComponent;
